<template>
    <div class="c-calendar" :class="{ 'c-calendar--loading' : isLoading }">
        <full-calendar
            ref="calendar"
            v-if="config.contentHeight"
            class="c-calendar__calendar"
            :events="events"
            :event-sources="eventSources"
            @event-selected="eventSelected"
            @event-drop="eventUpdateTime"
            @event-created="eventCreated"
            @day-click="dayClick"
            @event-resize="eventUpdateTime"
            :config="config">
        </full-calendar>

        <calendar-event-form-modal ref="eventFormModal" @success="refreshEvents"
                                   @cancel="refreshEvents"></calendar-event-form-modal>
        <calendar-event-show-modal ref="eventShowModal" @edit="editEvent"
                                   @event-deleted="refreshEvents"></calendar-event-show-modal>
    </div>

</template>

<script>
import {mapGetters} from 'vuex'
import store from 'Store'
import moment from 'moment'
import {FullCalendar} from 'vue-full-calendar'
import 'fullcalendar/dist/locale/pl'
import VLoader from 'Components/VLoader'
import CalendarEventFormModal from 'Components/Calendar/CalendarEventFormModal'
import CalendarEventShowModal from 'Components/Calendar/CalendarEventShowModal'

export default {

    name: 'CalendarMain',
    data() {
        return {
            config: {
                locale: 'pl',
                contentHeight: 0,
                allDaySlot: false,
                eventLimit: 4,
                slotDuration: '00:05:00',
                nowIndicator: true,
                eventOverlap: function(stillEvent, movingEvent) {
                    return stillEvent.rendering === 'background';
                },
                eventConstraint: {
                    start: '06:00',
                    end: '24:00',
                    dow: [1, 2, 3, 4, 5, 6, 0]
                },
                now: Date.now('yyyy-mm-dd hh:mm:ss'),
                showNonCurrentDates: false,
                fixedWeekCount: false,
                header: {
                    left: 'today',
                    center: 'prev,title,next',
                    right: 'month,agendaWeek,agendaDay'
                },
                buttonText: {
                    today: 'Idź do ' + moment().format('DD MMM YYYY')
                },
                defaultView: 'agendaWeek',
                slotLabelFormat: [
                    'MMMM YYYY',
                    'dd',
                    'HH:mm'
                ],
                minTime: '06:00:00',
                scrollTime: '08:00:00',
                businessHours:
                    {
                        start: '06:00',
                        end: '24:00',
                        dow: [1, 2, 3, 4, 5]
                    },
                loading: this.loading,
                windowResize: this.setCalendarHeight,
                selectLongPressDelay: 500,
                views: {
                    month: {
                        selectable: false
                    }
                }
            },
            isLoading: true,
            showModalEvent: false,
            eventSources: [
                {
                    events(start, end, timezone, callback) {
                        store.dispatch('calendar/FETCH_EVENTS', {
                            start: moment(start).format('YYYY-MM-DD'),
                            end: moment(end).format('YYYY-MM-DD')
                        }).then(() => {
                            callback([])
                        })
                    },
                }
            ]
        }
    },
    computed: {
        ...mapGetters('calendar', ['events']),
    },
    methods: {
        dayClick(date, jsEvent, view) {
            jsEvent.preventDefault()
            let event = {
                start: date,
                end: date,
                view: view
            }
            this.editEvent(event)
        },
        eventSelected(event, jsEvent, view) {
            this.$refs.eventShowModal.$emit('open', event)
        },
        eventCreated(event) {
            if (['agendaWeek', 'agendaDay'].indexOf(event.view.type) !== -1 &&
                moment(event.end).diff(event.start, 'minutes') <= 5)
                event.end = moment(event.start).add(1, 'hours')
            this.$refs.calendar.$emit('render-event', event)
            this.editEvent(event)
        },
        eventUpdateTime(event) {
            event.action = 'update-time'
            this.editEvent(event)
        },
        loading(isLoading, view) {
            this.isLoading = isLoading
        },
        refreshEvents() {
            this.$refs.calendar.$emit('refetch-events')
        },
        getEventById(id) {
            for (let i in this.events)
                if (this.events[i].id == id)
                    return this.events[i]

            return null
        },
        editEvent(event) {
            this.$refs.eventFormModal.$emit('open', event)
        },
        setCalendarHeight() {
            if (window.innerWidth > 991) {
                const toolbarHeight = 76;
                const height = this.$el.clientHeight - toolbarHeight
                this.config.contentHeight = (height < 400) ? 400 : height
            } else {
                this.config.contentHeight = 'auto'
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.setCalendarHeight()
        })
    },
    components: {
        FullCalendar,
        VLoader,
        CalendarEventFormModal,
        CalendarEventShowModal
    }
}
</script>

<style lang="scss">

@import '~Style/_utils.scss';

.c-calendar {
    width: 100%;

    .fc-header-toolbar .fc-center > div {
        display: flex;
    }

    .fc-view-container {
        overflow: auto;
    }

    @include bp($screen-md) {
        font-size: 13px;

        .fc-view {
            width: 100%;
        }
    }
    @include bp($screen-sm) {
        font-size: 12px;
    }
    @include bp(600px) {
        .fc-header-toolbar {
            display: flex;
            flex-flow: row wrap;

            .fc-left,
            .fc-right {
                float: none;
            }

            .fc-left {
                order: 1;
            }

            .fc-center {
                order: 2;
                flex-shrink: 999;
                margin-left: auto;

                h2 {
                    width: 100%;
                    text-align: right;
                }
            }

            .fc-right {
                order: 3;
                margin-top: 1.5em;
                width: 100%;

                .fc-button-group {
                    display: flex;
                    width: 100%;
                    justify-content: space-between;

                    .fc-button {
                        display: block;
                        width: 100%;
                        padding: .5em;
                    }
                }
            }
        }
    }

    .fc-view-container {
        position: relative;

        &:before,
        &:after {
            content: '';
            position: absolute;
            opacity: 0;
            visibility: hidden;
            transition: opacity .15s ease-in-out, visibility 0s .15s linear;
        }

        &:before {
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: $c-white;
            z-index: 4;
        }

        &:after {
            top: calc(50% - 25px);
            left: calc(50% - 25px);
            width: 50px;
            height: 50px;
            z-index: 5;
            background-color: $c-primary;
            border-radius: 100%;
            animation: calendar-loader 1.0s infinite ease-in-out;

            @keyframes calendar-loader {
                0% {
                    transform: scale(0);
                }
                100% {
                    transform: scale(1.0);
                    opacity: 0;
                }
            }
        }
    }

    &--loading .fc-view-container {
        &:before,
        &:after {
            visibility: visible;
            transition-delay: 0s;
            opacity: 0.9;
        }
    }
}

</style>
